import { Button, Divider, FormControl, FormLabel, HStack, Input, Stack, useToast } from "@chakra-ui/react";
import CDS from "design-system";
import { useState } from "react";
import GoogleIcon from "assets/GoogleIcon";
import { API_ROUTES } from "definitions/constants/routeConstants";
import apiClient from "services/ApiClient";
import { isNetworkingError } from "services/ApiClient/networkingError";

interface SignInOptionsFormProps {
  email: string;
  ssoUrl?: string;
  ssoProviderName?: string;
  ssoEnforced?: boolean;
  onMagicLinkSent?: () => void;
  onClose: () => void;
}

export default function SignInOptionsForm({
  email,
  ssoUrl,
  ssoProviderName,
  ssoEnforced = false,
  onMagicLinkSent,
  onClose,
}: SignInOptionsFormProps) {
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const showPasswordAuth = ["joincamino.com", "zoom.us", "okta.com"].some((domain) => email.trim().endsWith(domain));

  const handleSSOLogin = () => {
    if (ssoUrl) {
      window.location.href = ssoUrl;
    }
  };

  const handleMagicLink = async () => {
    setIsLoading(true);
    try {
      await apiClient.post(API_ROUTES.sessions.magicLinks.base, { email });
      if (onMagicLinkSent) {
        onMagicLinkSent();
      }
      toast({
        title: "Check your inbox",
        description:
          "If that email matches an account, we've sent you a link to sign in. Check your spam folder too. "
          + "If you don't see anything, reach out to your company or support@joincamino.com.",
        status: "success",
        duration: 8000,
      });
      onClose();
    } catch (error) {
      const errorMessage =
        isNetworkingError(error) && error.errors.length > 0
          ? error.errors[0]
          : "Please ensure you have entered a valid email address.";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    setIsLoading(true);
    try {
      await apiClient.post(API_ROUTES.sessions.passwords.base, { email, password });
      // Successful password login - reload the page to redirect to authenticated state
      window.location.reload();
    } catch (error) {
      const errorMessage =
        isNetworkingError(error) && error.errors.length > 0
          ? error.errors[0]
          : "Invalid email or password. Please try again.";

      toast({
        title: "Login Failed",
        description: errorMessage,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const params = new URLSearchParams({
      client_id: process.env.GOOGLE_OAUTH_CLIENT_ID || "",
      redirect_uri: `${window.location.origin}/api/v1/oauth/google/auth/callback`,
      response_type: "code",
      scope: "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile",
      access_type: "online",
    });

    window.location.href = `https://accounts.google.com/o/oauth2/auth?${params.toString()}`;
  };

  const getSSOProviderDisplayName = () => {
    if (!ssoProviderName) return "SSO";
    const providerNames: Record<string, string> = {
      okta: "Okta",
      azure_ad: "Microsoft Azure AD",
      google_workspace: "Google Workspace",
      onelogin: "OneLogin",
    };
    return providerNames[ssoProviderName] || "SSO";
  };

  return (
    <Stack spacing="4" pb="6">
      {ssoEnforced ? (
        <>
          <CDS.Text12 color="gray.600">
            Your organization requires you to sign in using {getSSOProviderDisplayName()}.
          </CDS.Text12>
          <Button size="lg" colorScheme="brand" onClick={handleSSOLogin}>
            Continue with {getSSOProviderDisplayName()}
          </Button>
        </>
      ) : (
        <>
          {ssoUrl && (
            <>
              <Button size="lg" colorScheme="brand" onClick={handleSSOLogin}>
                Continue with {getSSOProviderDisplayName()}
              </Button>

              <HStack>
                <Divider />
                <CDS.Text12 color="gray.500" whiteSpace="nowrap">
                  or
                </CDS.Text12>
                <Divider />
              </HStack>
            </>
          )}

          <Button leftIcon={<GoogleIcon />} onClick={handleGoogleLogin} variant="outline" size="lg" colorScheme="gray">
            Continue with Google
          </Button>

          {showPasswordAuth && (
            <>
              <FormControl>
                <FormLabel htmlFor="password">Password</FormLabel>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && password.trim()) {
                      handlePasswordLogin();
                    }
                  }}
                />
              </FormControl>
              <Button
                size="lg"
                variant="outline"
                onClick={handlePasswordLogin}
                isDisabled={!password.trim()}
                isLoading={isLoading}
              >
                Continue with Password
              </Button>
            </>
          )}

          <HStack>
            <Divider />
            <CDS.Text12 color="gray.500" whiteSpace="nowrap">
              or
            </CDS.Text12>
            <Divider />
          </HStack>

          <Button size="lg" variant="outline" onClick={handleMagicLink} isLoading={isLoading}>
            Continue with Magic Link
          </Button>
        </>
      )}
    </Stack>
  );
}
